import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowLeft, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Button } from '@/app/components/ui/button';
import { toast } from '@/app/components/ui/simple-toaster';
import { usePlans, useSubscription, useCreateCheckoutSession, useCreatePortalSession } from '@/hooks/use-billing';
import { usePermissions } from '@/hooks/use-permissions';
import { formatDateOnly } from '@/lib/date-utils';
import type { SubscriptionPlan, BillingCycle } from '@/lib/types';

const MODULE_NAMES: Record<string, string> = {
  inventory: 'H3Gear',
  safety: 'H3Safe',
  crew: 'H3Crew',
  projects: 'H3Books',
  work: 'H3Project',
  payroll: 'H3Pay',
  compliance: 'H3Comply',
};

const ALL_MODULES = ['inventory', 'safety', 'crew', 'projects', 'work', 'payroll', 'compliance'];

function isBundlePlan(plan: SubscriptionPlan): boolean {
  return plan.is_bundle;
}

function getSavingsPercent(plan: SubscriptionPlan): number | null {
  const modules = plan.modules_included || [];
  if (modules.length <= 1) return null;

  // Rough individual pricing by tier for calculating savings.
  // The slug suffix (e.g. h3-complete-starter) determines which tier
  // baseline to compare the bundle price against.
  const tier = plan.slug.split('-').pop();
  const individualPricesByTier: Record<string, Record<string, number>> = {
    starter: {
      inventory: 49,
      safety: 49,
      crew: 49,
      projects: 49,
      work: 49,
      payroll: 49,
      compliance: 79,
    },
    growth: {
      inventory: 99,
      safety: 99,
      crew: 99,
      projects: 99,
      work: 99,
      payroll: 99,
      compliance: 129,
    },
    pro: {
      inventory: 179,
      safety: 179,
      crew: 179,
      projects: 179,
      work: 179,
      payroll: 179,
      compliance: 209,
    },
  };

  const individualPrices = individualPricesByTier[tier || ''];
  if (!individualPrices) return null;

  const totalIndividual = modules.reduce((sum, m) => sum + (individualPrices[m] || 0), 0);
  const bundlePrice = parseFloat(plan.price_monthly);
  if (totalIndividual === 0) return null;

  const pct = Math.round((1 - bundlePrice / totalIndividual) * 100);
  return pct > 0 ? pct : null;
}

export function PlansPage() {
  const navigate = useNavigate();
  const { canManageOrganization } = usePermissions();
  const { data: plans, isLoading: plansLoading } = usePlans();
  const { data: subData } = useSubscription();
  const checkoutSession = useCreateCheckoutSession();
  const portalSession = useCreatePortalSession();
  const [selectedBillingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [subscribingPlanId, setSubscribingPlanId] = useState<number | null>(null);

  const annualAvailable = (plans || []).some((p) => p.has_annual_price);
  const billingCycle: BillingCycle = annualAvailable ? selectedBillingCycle : 'monthly';
  // Monthly-only plans stay on monthly billing even in Annual mode.
  const cycleFor = (plan: SubscriptionPlan): BillingCycle =>
    billingCycle === 'annual' && plan.has_annual_price ? 'annual' : 'monthly';
  const monthlyOnlyNote = (plan: SubscriptionPlan) =>
    billingCycle === 'annual' && !plan.has_annual_price ? (
      <p className="text-xs text-gray-500">Monthly only</p>
    ) : null;

  const currentSubscription = subData?.subscription;

  // A trialing org with no Stripe subscription behind it has not paid yet:
  // let it check out its current plan instead of showing a dead button.
  const needsPayment =
    currentSubscription?.status === 'trialing' && !currentSubscription.stripe_subscription_id;

  // A live Stripe subscription (paid trial, active, or past due) already
  // bills this org: checkout must not start a second one.
  const hasLiveSubscription =
    !!currentSubscription?.stripe_subscription_id &&
    ['trialing', 'active', 'past_due'].includes(currentSubscription.status);

  const handleManageBilling = async () => {
    if (!currentSubscription?.stripe_customer_id) {
      toast.error('Error', { description: 'No billing account linked. Please subscribe to a plan first.' });
      return;
    }
    try {
      const result = await portalSession.mutateAsync({
        return_url: window.location.href,
      });
      window.location.href = result.portal_url;
    } catch {
      toast.error('Error', { description: 'Could not open billing portal. Stripe may not be configured.' });
    }
  };

  const trialEndLine = (isCurrentPlan: boolean) =>
    isCurrentPlan && needsPayment && currentSubscription?.trial_end ? (
      <p className="text-center text-xs text-gray-500">
        Free trial ends {formatDateOnly(currentSubscription.trial_end)}
      </p>
    ) : null;

  const ctaLabel = (isCurrentPlan: boolean, plan: SubscriptionPlan, currentLabel: string) => {
    if (isCurrentPlan) {
      if (!needsPayment) return currentLabel;
      return subscribingPlanId === plan.id ? 'Redirecting...' : 'Add payment';
    }
    return subscribingPlanId === plan.id ? 'Redirecting...' : 'Subscribe';
  };

  const handleSubscribe = async (plan: SubscriptionPlan) => {
    if (hasLiveSubscription || plan.is_free) {
      return;
    }
    if (!canManageOrganization) {
      toast.error('Permission Denied', { description: 'Only administrators can change the subscription.' });
      return;
    }

    setSubscribingPlanId(plan.id);
    try {
      const result = await checkoutSession.mutateAsync({
        plan_id: plan.id,
        billing_cycle: cycleFor(plan),
        success_url: `${window.location.origin}/settings/subscription?checkout=success`,
        cancel_url: `${window.location.origin}/settings/subscription/plans`,
      });
      // eslint-disable-next-line react-hooks/immutability
      window.location.href = result.checkout_url;
    } catch (err) {
      const serverMessage =
        axios.isAxiosError(err) && err.response?.status === 409
          ? (err.response.data as { error?: string })?.error
          : undefined;
      toast.error('Error', {
        description:
          serverMessage ||
          'Could not start checkout. Stripe may not be configured yet. Please contact support.',
      });
      setSubscribingPlanId(null);
    }
  };

  if (plansLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-1/4" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  const freePlans = (plans || []).filter((p) => p.is_free);
  const individualPlans = (plans || []).filter((p) => !p.is_free && !isBundlePlan(p));
  const bundlePlans = (plans || []).filter((p) => !p.is_free && isBundlePlan(p));

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/settings/subscription')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Choose Your Plan</h1>
          <p className="text-gray-600 mt-1">Select the modules your team needs</p>
        </div>
      </div>

      {/* Existing subscription notice */}
      {hasLiveSubscription && (
        <div className="flex items-center justify-between flex-wrap gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <p className="text-sm text-blue-900">
            You already have a subscription. Change plans from Manage billing.
          </p>
          {canManageOrganization && currentSubscription?.stripe_customer_id && (
            <Button size="sm" variant="outline" onClick={handleManageBilling} disabled={portalSession.isPending}>
              {portalSession.isPending ? 'Opening...' : 'Manage billing'}
            </Button>
          )}
        </div>
      )}

      {/* Billing Toggle: only when a plan on screen has an annual Stripe price */}
      {annualAvailable && (
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              billingCycle === 'monthly'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              billingCycle === 'annual'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Annual
            <span className="ml-1.5 text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full">
              Save 2 months
            </span>
          </button>
        </div>
      )}

      {/* Free plan: shown as included, never sent to checkout */}
      {freePlans.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {freePlans.map((plan) => (
            <Card key={plan.id} data-testid="free-plan-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{plan.name}</CardTitle>
                <p className="text-sm text-gray-500">{plan.description}</p>
              </CardHeader>
              <CardContent>
                <span className="text-2xl font-bold text-gray-900">Free — included</span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Bundle Plans */}
      {bundlePlans.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-yellow-500" />
            Bundle Packages
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {bundlePlans.map((plan) => {
              const isCurrentPlan = currentSubscription?.plan_detail.slug === plan.slug;
              const savings = getSavingsPercent(plan);
              const isComplete = plan.slug.startsWith('h3-complete');
              const cycle = cycleFor(plan);
              const price = cycle === 'annual' ? plan.price_annual : plan.price_monthly;

              return (
                <Card
                  key={plan.id}
                  className={`relative ${isComplete ? 'border-2 border-blue-500 shadow-lg' : ''}`}
                >
                  {isComplete && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <CardHeader className="text-center pb-2">
                    <CardTitle className="text-lg">{plan.name}</CardTitle>
                    <p className="text-sm text-gray-500">{plan.description}</p>
                    {savings !== null && savings > 0 && (
                      <span className="inline-block mt-1 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                        Save {savings}%
                      </span>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-center">
                      <span className="text-3xl font-bold text-gray-900">${price}</span>
                      <span className="text-gray-500 text-sm">
                        /{cycle === 'annual' ? 'year' : 'month'}
                      </span>
                      {monthlyOnlyNote(plan)}
                    </div>

                    <div className="space-y-2">
                      {ALL_MODULES.map((modKey) => {
                        const included = (plan.modules_included || []).includes(modKey);
                        // Bundle plans with empty modules_included: H3Gear is
                        // always on; other modules are chosen at onboarding.
                        const isFlexibleBundle =
                          plan.is_bundle && (plan.modules_included || []).length === 0;
                        const alwaysIncluded = isFlexibleBundle && modKey === 'inventory';
                        const choosable = isFlexibleBundle && modKey !== 'inventory';

                        return (
                          <div
                            key={modKey}
                            className={`flex items-center gap-2 text-sm ${
                              included || alwaysIncluded
                                ? 'text-gray-900'
                                : choosable
                                  ? 'text-gray-600'
                                  : 'text-gray-400 line-through'
                            }`}
                          >
                            <Check
                              className={`h-4 w-4 flex-shrink-0 ${
                                included || alwaysIncluded
                                  ? 'text-green-600'
                                  : choosable
                                    ? 'text-blue-400'
                                    : 'text-gray-300'
                              }`}
                            />
                            {MODULE_NAMES[modKey] || modKey}
                            {choosable && (
                              <span className="text-xs text-blue-500 ml-auto">Choose</span>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {trialEndLine(isCurrentPlan)}
                    {canManageOrganization && (
                      <Button
                        className="w-full"
                        variant={isComplete ? 'default' : 'outline'}
                        disabled={
                          (isCurrentPlan && !needsPayment) ||
                          hasLiveSubscription ||
                          subscribingPlanId === plan.id
                        }
                        onClick={() => handleSubscribe(plan)}
                      >
                        {ctaLabel(isCurrentPlan, plan, 'Current Plan')}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Individual Modules */}
      {individualPlans.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Individual Modules</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {individualPlans.map((plan) => {
              const isCurrentPlan = currentSubscription?.plan_detail.slug === plan.slug;
              const cycle = cycleFor(plan);
              const price = cycle === 'annual' ? plan.price_annual : plan.price_monthly;

              return (
                <Card key={plan.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{plan.name}</CardTitle>
                    <p className="text-xs text-gray-500">{plan.description}</p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <span className="text-2xl font-bold text-gray-900">${price}</span>
                      <span className="text-gray-500 text-xs">
                        /{cycle === 'annual' ? 'yr' : 'mo'}
                      </span>
                      {monthlyOnlyNote(plan)}
                    </div>

                    <div className="space-y-1">
                      {(plan.modules_included || []).map((modKey) => (
                        <div key={modKey} className="flex items-center gap-1.5 text-xs text-gray-700">
                          <Check className="h-3 w-3 text-green-600" />
                          {MODULE_NAMES[modKey] || modKey}
                        </div>
                      ))}
                    </div>

                    {trialEndLine(isCurrentPlan)}
                    {canManageOrganization && (
                      <Button
                        className="w-full"
                        variant="outline"
                        size="sm"
                        disabled={
                          (isCurrentPlan && !needsPayment) ||
                          hasLiveSubscription ||
                          subscribingPlanId === plan.id
                        }
                        onClick={() => handleSubscribe(plan)}
                      >
                        {ctaLabel(isCurrentPlan, plan, 'Current')}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Feature Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Feature Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Module</th>
                  {(plans || []).filter(isBundlePlan).map((plan) => (
                    <th key={plan.id} className="text-center py-3 px-2 font-medium text-gray-900">
                      {plan.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ALL_MODULES.map((modKey) => (
                  <tr key={modKey} className="border-b last:border-0">
                    <td className="py-3 px-2 text-gray-700">{MODULE_NAMES[modKey]}</td>
                    {bundlePlans.map((plan) => {
                      const included = (plan.modules_included || []).includes(modKey);
                      const isFlexibleBundle =
                        plan.is_bundle && (plan.modules_included || []).length === 0;
                      const alwaysIncluded = isFlexibleBundle && modKey === 'inventory';
                      const choosable = isFlexibleBundle && modKey !== 'inventory';

                      return (
                        <td key={plan.id} className="text-center py-3 px-2">
                          {included || alwaysIncluded ? (
                            <Check className="h-4 w-4 text-green-600 mx-auto" />
                          ) : choosable ? (
                            <Check className="h-4 w-4 text-blue-400 mx-auto" />
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="border-t-2">
                  <td className="py-3 px-2 font-medium text-gray-900">
                    Price ({billingCycle === 'annual' ? 'Annual' : 'Monthly'})
                  </td>
                  {bundlePlans.map((plan) => (
                    <td key={plan.id} className="text-center py-3 px-2 font-bold text-gray-900">
                      ${cycleFor(plan) === 'annual' ? plan.price_annual : plan.price_monthly}
                      {monthlyOnlyNote(plan)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* FAQ */}
      <Card>
        <CardHeader>
          <CardTitle>Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-900">Can I change plans later?</h4>
            <p className="text-sm text-gray-600 mt-1">
              Yes! You can upgrade or downgrade at any time. When upgrading, you'll be charged the prorated
              difference. When downgrading, your new rate takes effect at the next billing period.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900">What happens when my trial ends?</h4>
            <p className="text-sm text-gray-600 mt-1">
              When your free trial ends, add payment to keep your modules.
              No credit card required to start a trial.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900">Is there a free tier?</h4>
            <p className="text-sm text-gray-600 mt-1">
              The H3ightboard (operations board) is always free. Premium modules like H3Gear, H3Safe, H3Crew,
              H3Books, H3Project, H3Pay, and H3Comply require a paid subscription.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900">How does annual billing work?</h4>
            <p className="text-sm text-gray-600 mt-1">
              Annual plans are billed once per year and include 2 months free compared to monthly billing.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
